// Stefans-AI-CV-frontend/src/sections/Projects.tsx

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useI18n } from '../i18n'
import { analyticsService } from '../services/analytics'

type Category = 'all' | 'defi' | 'ai' | 'quant' | 'research'

interface ProjectItem {
  id: string
  title: string
  subtitle: string
  desc: string
  tags: string[]
  category: string
  highlight: string
  link: string | null
}

const FEATURED_IDS = ['hl-research', 'kayaland']

function ProjectCard({ item }: { item: ProjectItem }) {
  const t = useI18n()
  const trackProjectClick = () => {
    analyticsService.trackEvent('project_link_click', {
      category: 'projects',
      label: item.id,
      project_id: item.id,
      project_title: item.title,
      project_category: item.category,
      is_featured: false,
    })
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, layout: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } }}
      className="relative glass-card rounded-2xl p-5 hover:border-border-strong transition-all duration-300 group hover:-translate-y-1 hover:glow-cyan flex flex-col"
    >
      {/* Highlight badge */}
      <div className="absolute top-4 right-4">
        <span className="text-xs font-mono font-semibold text-cyan-400 bg-cyan-dim px-2 py-0.5 rounded-md border border-cyan-400/20">
          {item.highlight}
        </span>
      </div>

      <h3 className="font-heading font-semibold text-text-primary text-base mb-1 group-hover:text-cyan-400 transition-colors pr-20">
        {item.title}
      </h3>
      <p className="text-text-muted text-xs mb-3 font-mono">{item.subtitle}</p>
      <p className="text-text-secondary text-sm leading-relaxed flex-1 mb-4">{item.desc}</p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {item.tags.map((tag, i) => (
          <span
            key={i}
            className="px-2 py-0.5 text-xs rounded-md bg-white/5 text-text-muted border border-border"
          >
            {tag}
          </span>
        ))}
      </div>

      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackProjectClick}
          className="flex items-center gap-1.5 text-cyan-400 text-xs hover:text-cyan-300 transition-colors mt-auto w-fit"
        >
          <span>{t.projects.view}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      )}
    </motion.div>
  )
}

function FeaturedProjectCard({ item, index }: { item: ProjectItem; index: number }) {
  const t = useI18n()
  const trackProjectClick = () => {
    analyticsService.trackEvent('project_link_click', {
      category: 'projects',
      label: item.id,
      project_id: item.id,
      project_title: item.title,
      project_category: item.category,
      is_featured: true,
      featured_rank: index + 1,
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative glass-card rounded-2xl p-7 hover:border-border-strong transition-all duration-300 group hover:-translate-y-1 hover:glow-cyan flex flex-col"
    >
      {/* Featured badge */}
      <div className="absolute top-5 right-5 flex items-center gap-1.5">
        <span className="text-xs font-mono font-semibold text-cyan-400 bg-cyan-dim px-2 py-0.5 rounded-md border border-cyan-400/20">
          {item.highlight}
        </span>
        <span className="text-[10px] font-mono text-text-muted bg-white/5 px-1.5 py-0.5 rounded border border-border uppercase tracking-wide">
          Featured
        </span>
      </div>

      <h3 className="font-heading font-bold text-text-primary text-xl mb-1 group-hover:text-cyan-400 transition-colors pr-32">
        {item.title}
      </h3>
      <p className="text-text-muted text-xs mb-4 font-mono">{item.subtitle}</p>
      <p className="text-text-secondary text-sm leading-relaxed flex-1 mb-5">{item.desc}</p>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {item.tags.map((tag, i) => (
          <span
            key={i}
            className="px-2.5 py-1 text-xs rounded-md bg-cyan-400/5 text-cyan-400/80 border border-cyan-400/15"
          >
            {tag}
          </span>
        ))}
      </div>

      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackProjectClick}
          className="flex items-center gap-1.5 text-cyan-400 text-xs hover:text-cyan-300 transition-colors mt-auto w-fit"
        >
          <span>{t.projects.view}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      )}
    </motion.div>
  )
}

export default function Projects() {
  const t = useI18n()
  const [activeFilter, setActiveFilter] = useState<Category>('all')

  const allItems = t.projects.items as ProjectItem[]
  const featuredItems = allItems.filter((item) => FEATURED_IDS.includes(item.id))
  // Sort featured to match FEATURED_IDS order
  featuredItems.sort((a, b) => FEATURED_IDS.indexOf(a.id) - FEATURED_IDS.indexOf(b.id))
  const restItems = allItems.filter((item) => !FEATURED_IDS.includes(item.id))

  const filters: { key: Category; label: string }[] = [
    { key: 'all', label: t.projects.all },
    { key: 'defi', label: t.projects.defi },
    { key: 'ai', label: t.projects.ai },
    { key: 'quant', label: t.projects.quant },
    { key: 'research', label: t.projects.research },
  ]

  // 'all' 只展示非精选卡（精选已在上方展示），特定分类则全量筛选（含精选）避免空白
  const filtered = activeFilter === 'all'
    ? restItems
    : allItems.filter((item) => item.category === activeFilter)

  return (
    <section id="projects" className="py-24 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className="text-cyan-400 text-sm font-mono mb-2">// projects</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-3">
            {t.projects.title}
          </h2>
          <p className="text-text-secondary">{t.projects.subtitle}</p>
        </motion.div>

        {/* Featured projects — 2 big cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {featuredItems.map((item, i) => (
            <FeaturedProjectCard key={item.id} item={item} index={i} />
          ))}
        </div>

        {/* Section divider */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="flex-1 h-px bg-border" />
          <span className="text-text-muted text-xs font-mono uppercase tracking-wider">More Projects</span>
          <div className="flex-1 h-px bg-border" />
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-2 mb-8"
        >
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                analyticsService.trackEvent('project_filter_click', {
                  category: 'projects',
                  label: f.key,
                  filter: f.key,
                })
                setActiveFilter(f.key)
              }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeFilter === f.key
                  ? 'bg-cyan-400/15 text-cyan-400 border border-cyan-400/30'
                  : 'text-text-secondary border border-border hover:border-border-strong hover:text-text-primary hover:bg-white/5'
              }`}
            >
              {f.label}
            </button>
          ))}
        </motion.div>

        {/* Grid — outer motion.div handles height change, inner cards handle position+opacity */}
        <motion.div layout transition={{ layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout" initial={false}>
              {(filtered as ProjectItem[]).map((item) => (
                <ProjectCard key={item.id} item={item} />
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
